import client_lookups from '@/assets/json/clientLookups.json'
import i18n from '@/i18n/i18n'
import axiosInstance from '@/services'
import { useAuthStore } from '@/stores/auth'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import { useLookupsStore } from '@/stores/lookups'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import router from '@/router'

export const usePreloadStore = defineStore('preloadStore', () => {
  const states = {
    estabInfo: null
  }

  const authStore = useAuthStore()
  const formStore = useFormStore()
  const lookupsStore = useLookupsStore()
  const globalStore = useGlobalStore()

  const { serviceTransactionId } = globalStore.startedService

  const prepareForm = async () => {
    let params = {}
    if (serviceTransactionId === 1120) {
      Object.assign(params, {
        params: {
          establishmentId: authStore.user.establishmentId,
          getAllRelatedPersonFlag: 1
        }
      })
    }

    if (serviceTransactionId === 1120 || !authStore?.establishmentInfo) {
      await authStore.fetchEstablishmentInfo(params)
    }

    const {
      englishName,
      arabicName,
      department,
      sponsorType,
      licenseIssueDate,
      number,
      cardExpireDate,
      departmentID,
      cardIssueDate,
      licenseNumber,
      licenseExpiryDate,
      type,
      economicSectorId,
      licensingAuthorityId,
      licenseTypeID,
      nationalityID,
      address,
      buldingNumber,
      estabShopNumber,
      emirateID,
      phoneNumber,
      faxNumber,
      commericalActivitiesId,
      investorUnifiedNumber,
      investorPhoneNumber,
      sponsorTypeCode
    } = authStore.establishmentInfo

    Object.assign(formStore.draft.request, {
      hostEnglishName: englishName,
      hostArabicName: arabicName,
      departmentText: department.text,
      sponsorTypeText: sponsorType.text,
      licenseIssueDate:
        licenseIssueDate && DateTime.fromJSDate(new Date(licenseIssueDate)).toFormat('dd/MM/yyyy'),
      number: number,
      sponsorName: i18n.global.locale.value == 'en' ? englishName : arabicName,
      sponsorNumber: number,
      sponsorNumberNew: number,
      sponsorEnglishNameNew: englishName,
      sponsorArabicNameNew: arabicName,
      sponsorTypeCode,
      englishEstablishmentName: englishName,
      arabicEstablishmentName: arabicName,
      cardExpiryDate: cardExpireDate,
      immigrationDepartmentId: departmentID,
      establishmentNumber: number,
      cardIssueDate: cardIssueDate,
      establishmentLicenceNumber: licenseNumber,
      establishmentLicenceIssueDate: licenseIssueDate,
      establishmentLicenceExpiryDate: licenseExpiryDate,
      establishmentTypeId: type,
      workSectorId: economicSectorId,
      establishmentLicensePlaceId: licensingAuthorityId,
      establishmentLicenseTypeId: licenseTypeID,
      establishmentNationalityId: nationalityID,
      workAddress: {
        name: address
      },
      buildingName: buldingNumber,
      shopNumber: estabShopNumber,
      emirateId: emirateID,
      localAddress: {
        name: address
      },
      economicActivityId: commericalActivitiesId,
      investorUnifiedNumber
    })

    Object.assign(formStore.extraMap, {
      serviceTransactionId: serviceTransactionId,
      establishmentId: authStore.establishmentInfo.id
    })

    if (phoneNumber) {
      if (phoneNumber.startsWith('00971')) {
        formStore.draft.request.workTelephone = phoneNumber
      }

      if (phoneNumber.startsWith('05') || phoneNumber.startsWith('02')) {
        const phoneNumberTemp = phoneNumber.substring(1)
        formStore.draft.request.workTelephone = `00971${phoneNumberTemp}`
      }
    }

    if (investorPhoneNumber) {
      if (investorPhoneNumber.startsWith('00971')) {
        formStore.draft.request.investorPhoneNumber = investorPhoneNumber
      }

      if (investorPhoneNumber.startsWith('05') || investorPhoneNumber.startsWith('02')) {
        const investorPhoneNumberTemp = investorPhoneNumber.substring(1)
        formStore.draft.request.investorPhoneNumber = `00971${investorPhoneNumberTemp}`
      }
    }

    if (faxNumber) {
      if (faxNumber.startsWith('00971')) {
        formStore.draft.request.faxNumber = faxNumber
      } else {
        formStore.draft.request.faxNumber = `00971${faxNumber}`
      }
    }

    // load cities for this emirate and set the cityId
    const path = `/api/lookup/emirate/${emirateID}/city`
    const cities = await lookupsStore.fetchLookup(path)
    formStore.draft.request.cityId = cities.find((c) => c.mainCityId === true)?.id

    formStore.form.setValues({ ...formStore.draft.request })
  }

  const fetchEstablishmentFines = async () => {
    const globalStore = useGlobalStore()
    const formStore = useFormStore()

    const { immigrationDepartmentId, establishmentNumber, delegateCardSequence, delegateCardYear } =
      formStore.form.values

    const body = {
      serviceTransactionId: globalStore.startedService.serviceTransactionId,
      immigrationDepartmentId,
      establishmentNumber,
      delegateCardSequence,
      delegateCardYear
    }
    const response = await axiosInstance.post('/api/fines/draft/calculateEstabFines', body)
    const { finesAmount, overstayDays } = response
    Object.assign(formStore.draft.request, {
      finesAmount,
      overstayDays
    })

    formStore.form.setValues({ ...formStore.draft.request })
  }

  async function fetchEstablishmentAddress() {
    if (formStore.draft.requestNumber) {
      return
    }

    const params = {
      childEstabId: null
    }

    const establishmentAddress = await axiosInstance.get(
      '/api/addressesBook/formComponent/mapAndPrepareAddressForVisaRequest',
      { params }
    )

    Object.assign(formStore.draft.request, establishmentAddress)
    formStore.form.setValues({ ...formStore.draft.request })
  }

  async function mapAndPrepareAddressForEidaRequest() {
    const formStore = useFormStore()
    const response = await axiosInstance.get(
      '/api/addressesBook/formComponent/mapAndPrepareAddressForEidaRequest'
    )

    const {
      localUserAddressId,
      isHaveNewBehaviorLocalAddress,
      localAddressSourceEnum,
      localAddressUserActionEnum,
      localEmirateId,
      localCityId,
      localAreaId,
      localAddress,
      localHomePhone,
      localMobile,
      emailAddress,
      cityId,
      localPobox,
      buildingName,
      flatNumber
    } = response

    // local address
    Object.assign(formStore.draft, {
      request: {
        ...formStore.draft.request,
        localUserAddressId,
        isHaveNewBehaviorLocalAddress,
        localAddressSourceEnum,
        localAddressUserActionEnum,
        localEmirateId,
        localCityId,
        localAreaId,
        localAddress,
        localHomePhone,
        localMobile,
        emailAddress,
        cityId,
        localPobox,
        buildingName,
        flatNumber
      }
    })

    const {
      IsHaveNewBehaviorDeliveryAddress,
      deliveryAddressSourceEnum,
      deliveryUserAddressId,
      deliveryAddressUserActionEnum,
      deliveryMethodId,
      deliveryCompanyId,
      currentMobileNumber,
      secondMobileNumber,
      deliveryMobileNumber,
      deliveryAddressDetails,
      tawzeaDeliveryEmirateId,
      tawzeaDeliveryCityId,
      tawzeaDeliveryAreaId,
      landMarkId,
      postalBranchId,
      emiratePostDeliveryEmirateId,
      emiratePostDeliveryCityId,
      emiratePostDeliveryAreaId,
      havePoBox,
      sponsoredPobox,
      deliveryLocalMobile,
      zajelDeliveryEmirateId,
      zajelDeliveryCityId,
      zajelDeliveryAreaId,
      amerDeliveryEmirateId,
      amerDeliveryCityId,
      amerDeliveryAreaId
    } = response

    // delivery address
    Object.assign(formStore.draft, {
      request: {
        ...formStore.draft.request,
        IsHaveNewBehaviorDeliveryAddress,
        deliveryAddressSourceEnum,
        deliveryUserAddressId,
        deliveryAddressUserActionEnum,
        deliveryMethodId,
        deliveryCompanyId,
        currentMobileNumber,
        secondMobileNumber,
        deliveryMobileNumber,
        deliveryAddressDetails,
        tawzeaDeliveryEmirateId,
        tawzeaDeliveryCityId,
        tawzeaDeliveryAreaId,
        landMarkId,
        postalBranchId,
        emiratePostDeliveryEmirateId,
        emiratePostDeliveryCityId,
        emiratePostDeliveryAreaId,
        havePoBox,
        sponsoredPobox,
        deliveryLocalMobile,
        zajelDeliveryEmirateId,
        zajelDeliveryCityId,
        zajelDeliveryAreaId,
        amerDeliveryEmirateId,
        amerDeliveryCityId,
        amerDeliveryAreaId
      }
    })

    const {
      isHaveNewBehaviorAbroadAddress,
      abroadAddressSourceEnum,
      abroadUserAddressId,
      abroadAddressUserActionEnum,
      abroadCountryId,
      abroadPhone,
      abroadAddress
    } = response

    // abroad address
    Object.assign(formStore.draft, {
      request: {
        ...formStore.draft.request,
        isHaveNewBehaviorAbroadAddress,
        abroadAddressSourceEnum,
        abroadUserAddressId,
        abroadAddressUserActionEnum,
        abroadCountryId,
        abroadPhone,
        abroadAddress
      }
    })
  }

  async function prepareIcaForm() {
    const formStore = useFormStore()

    const {
      passportRelation: { holder },
      applicatClassType: { expat },
      applicationRequestType: { newIDCard },
      applicantClassDetails: { NewResidency, NewBorn }
    } = client_lookups

    const { hasPoBox, workTypeId, personUnifiedNumber, applicantClassDetailId } =
      formStore.draft.request

    Object.assign(formStore.draft.request, {
      passportRelationId: holder,
      applicantClassId: expat,
      applicationRequestTypeId: newIDCard,
      hasPoBox: hasPoBox ? 1 : 0,
      applicantClassDetailId: NewBorn
    })

    if (!workTypeId && personUnifiedNumber) {
      const path = `/api/icaRequest/draft/getInitialData/${personUnifiedNumber}`
      const result = await axiosInstance.get(path)
      formStore.draft.request.workTypeId = result.workTypeId
    }

    if (applicantClassDetailId == NewResidency) {
      formStore.draft.request.showidentityNumberField = false
      Object.assign(formStore.extraMap, {
        identityNumberRequired: '',
        disableOldEidaExpiryDate: false,
        oldEidaExpiryDateRequired: '',
        currentResidenceDateRequired: ''
      })
    }
  }
  async function getFormDetails() {
    const formStore = useFormStore()
    const globalStore = useGlobalStore()
    const { serviceTransactionId } = globalStore.startedService
    const {
      fileNumber = '',
      moduleId = '',
      withException = '',
      expD = '',
      employeeUN = ''
    } = router.currentRoute.value.query
    formStore.extraMap = {
      ...formStore.extraMap,
      serviceTransactionId,
      moduleId,
      expD,
      employeeUN,
      withException: withException == 'true'
    }
    if (!fileNumber) return
    const path = `/api/establishment/visa/${fileNumber}`
    const result = await axiosInstance.get(path)
    if (!result) return
    formStore.draft.request = result
  }

  const getters = {}
  const actions = {
    prepareForm,
    fetchEstablishmentFines,
    fetchEstablishmentAddress,
    mapAndPrepareAddressForEidaRequest,
    prepareIcaForm,
    getFormDetails
  }

  return {
    ...states,
    ...getters,
    ...actions
  }
})

export default async function () {
  const authStore = useAuthStore()
  if (authStore.user.applicationId == 2) {
    return
  }
  const globalStore = useGlobalStore()
  const preloadStore = usePreloadStore()

  await preloadStore.prepareForm()

  const { serviceTransactionId } = globalStore.startedService

  // if you need _FileStatusId in your request
  const includeEstAddress = [380, 691]
  if (includeEstAddress.includes(serviceTransactionId)) {
    await preloadStore.fetchEstablishmentAddress()
  }

  if (serviceTransactionId === 440) {
    await preloadStore.fetchEstablishmentFines()
  }

  if (serviceTransactionId == 870) {
    await preloadStore.prepareIcaForm()
    await preloadStore.mapAndPrepareAddressForEidaRequest()
  }

  if (serviceTransactionId == 413) {
    await preloadStore.getFormDetails()
  }
}
